import { useStore } from "csi/state/hooks";
import { rightPaneStore } from "csi/state/rootStore";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import { Button, ConfirmDialog, FlexColumn, FlexRowCenter, MenuItem, MoreHorizMenu } from "designSystem/index";
import { white } from "designSystem/styles/color";
import { POPOVER_TOP_END } from "designSystem/styles/popover-positions";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { FunctionComponent, useMemo } from "react";
import NewSnapshotTemplateDialog from "../snapshotTemplates/dialogs/NewSnapshotTemplateDialog";
import { useSnapshotTemplateStore } from "../snapshotTemplates/SnapshotTemplateStore";
import TemplateForm from "../snapshotTemplates/templateForm/TemplateForm";
import { LocalGenerateImageStore, LocalGenerateImageStoreContext, useLocalGenerateImageStore } from "./GenerateImagesStore";

const SaveAndGenerateFooter: FunctionComponent = observer((props) => {
	const { 
		imageOptions, 
		rendererOptions, 
		templateOptions, 
		userCanSaveTemplate, 
		isSaveAsNewModalOpen, 
		openModal,
		dialogStore,
	 } = useLocalGenerateImageStore();
	const { saveTemplate } = useSnapshotTemplateStore();
	const { threeDStore: { snapshotsStore, has3DLibraryAdminRole } } = useStore();
	
	const { 
		canGenerateImages,
		generateImages,
		numberOfGenerateImages,
	} = snapshotsStore;	

	const handleGenerateImages = () => {
		generateImages(imageOptions, rendererOptions);
	};

	const numberOfImages = useMemo(() => {
		return numberOfGenerateImages(imageOptions);
	}, [imageOptions]);

	const numberOfImagesText = numberOfImages > 0 ? `(${numberOfImages})` : "";
	
	const userCanGenerateImages = useMemo(() => {
		return canGenerateImages(imageOptions, rendererOptions) && numberOfImages > 0;
	}, [imageOptions, rendererOptions, numberOfImages]);

	const confirmSaveTemplate = () => {
		rightPaneStore.openDialog(ConfirmDialog, {
			category: "info",
			confirmRes: "Save",
			messageRes: "3DSaveTemplateChange",
			messageResParams: [templateOptions.templateName],
			onConfirm: saveTemplate,
			showClose: true,
			titleRes: "SaveChanges",
		});
	};

	return (
		<FlexColumn
			bgcolor={white[50]}
			borderTop={STANDARD_BORDER_STYLE}
			bottom={0}
			justifyContent="center"
			p={3}
			position="sticky"
			sx={{
				height: "auto",
			}}
		>
			<FlexRowCenter gap={2}>
				<Button 
					design="primary"
					disabled={!userCanGenerateImages}
					fullWidth
					onClick={handleGenerateImages}
					res="3DGenerate"
					resParams={[numberOfImagesText]}
				/>
				{ has3DLibraryAdminRole && (
					<>
						<MoreHorizMenu 
							disabled={!userCanGenerateImages}
							menuProps={{ 
								PaperProps: {
									sx: {
										mt: -2,
									},
								},
								...POPOVER_TOP_END }}
						>
							<MenuItem
								disabled={!userCanSaveTemplate || !userCanGenerateImages}
								onClick={confirmSaveTemplate}
								res="SaveTemplate"
							/>
							<MenuItem
								disabled={!userCanGenerateImages}
								onClick={openModal}
								res="SaveAsNewTemplate"
							/>
						</MoreHorizMenu>
						<NewSnapshotTemplateDialog
							autoPopulatedImageOptions={imageOptions}
							autoPopulatedRendererOptions={rendererOptions}
							autoPopulatedTemplateOptions={templateOptions}
							open={isSaveAsNewModalOpen}
							store={dialogStore}
						/>
					</>
				)}
			</FlexRowCenter>

		</FlexColumn>
	);
});

export const GenerateImages = observer(() => {
	const { rightPaneStore } = useStore();
	const { closeSelected } = rightPaneStore;
	const { threeDStore } = useStore();
	const { snapshotsStore, snapshotTemplateId } = threeDStore;
	const { 
		toggleGenerateImagesPage,
	} = snapshotsStore;
	const localStore = useLocalObservable(() => new LocalGenerateImageStore(snapshotTemplateId));
	const { setImageOptions, setRendererOptions, setTemplateOptions, setUserCanSaveTemplate, setDataHasChanged } = localStore;

	return (
		<LocalGenerateImageStoreContext.Provider value={localStore}>
			<FlexColumn>
				<PaneHeader
					back
					onBack={toggleGenerateImagesPage}
					onClose={closeSelected}
					titleRes="GenerateImages"
				/>
				<FlexColumn justifyContent="space-between">
					<TemplateForm
						actions={(
							<SaveAndGenerateFooter />
						)}
						id={snapshotTemplateId}
						isTemplateAutoPopulated
						setDataHasChanged={setDataHasChanged}
						setImageOptionsCallback={setImageOptions}
						setRendererOptionsCallback={setRendererOptions}
						setTemplateOptionsCallback={setTemplateOptions}
						setUserCanSaveTemplate={setUserCanSaveTemplate}
					/>
				</FlexColumn>
			</FlexColumn>
		</LocalGenerateImageStoreContext.Provider>
	);
});